import React from 'react';

import type { IViewModel } from 'mobx-utils';

import type { Constructable } from 'bz-utilities/ts';

export class ContextNotProvidedError extends Error {
  constructor(name: string) {
    super(`[${name}] - Context has not been provided`);
  }
}

const createContext = function <T>(name: string): [React.Context<T>, () => T] {
  const symbol = Symbol(name);
  const context = React.createContext<T | symbol>(symbol);
  context.displayName = name;

  const useContext = (): T => {
    const value = React.useContext(context);

    if (value === symbol) {
      throw new ContextNotProvidedError(name);
    }

    return value as T;
  };

  return [context as React.Context<T>, useContext];
};

export const createContextForDispatch = function <T>(name: string) {
  return createContext<React.Dispatch<T>>(name);
};

export const createContextForClass = function <T extends object>(clazz: Constructable<T>) {
  return createContext<T>(`${clazz.name}`);
};

export const createContextForClassArray = function <T extends object>(clazz: Constructable<T>) {
  return createContext<T[]>(`${clazz.name}Array`);
};

export const createContextForEntry = function <T extends object, K extends keyof T>(
  clazz: Constructable<T>,
  key: K
) {
  return createContext<T[K]>(`${clazz.name}[${String(key)}]`);
};

export default createContext;
